import BaseClass from "../../util/BaseClass";
import Context from "../../context/Context";
import FieldNames from "../../aiim/datasets/FieldNames";
import OfficePlan, { SpaceAssignmentTypes } from "./OfficePlan";
import Topic from "../../context/Topic";
import * as aiimUtil from "../../aiim/util/aiimUtil";
import * as transactions from "./transaction/transactions";
import * as val from "../../util/val";
import { ILayerDefinition, IFeature } from "@esri/arcgis-rest-types";
import type { CustomQueryTask } from "../../context/EsriLib";

/*
  Transactions

    addArea
    deleteArea
    updateArea

 */
export const HomeOfficeName = "Home Office";
export const HomeOfficeId = "0";

export const HomeOfficeJSON: IFeature = Object.freeze({
  attributes: Object.freeze({
    [FieldNames.AREA_ID]: HomeOfficeId,
    [FieldNames.AREA_NAME]: HomeOfficeName,
    [FieldNames.AREA_TYPE]: SpaceAssignmentTypes.home
  })
})
export default class AreasTable extends BaseClass {

  table: __esri.FeatureLayer;

  _areasById: { [props: string]: __esri.Graphic };
  _hotDeskAreas: __esri.Graphic[];
  _hotelAreas: __esri.Graphic[];

  readonly HomeOffice!: __esri.Graphic;

  constructor() {
    super();
    this.HomeOffice = Context.getInstance().lib.esri.Graphic.fromJSON(HomeOfficeJSON);
  }
  static getConfig(feature: __esri.Graphic) {

    const config: {
      maxBookingsPerPerson: number,
      maxDaysPerBooking: number,
      maxDaysInAdvance: number,
      autoCancelMinutes: number,
      autoCancelMinutesAllDay: number
    } = {

      // Hotel settings: ignore if not a number or < 1
      maxBookingsPerPerson: null,
      maxDaysPerBooking: null,
      maxDaysInAdvance: null,
      autoCancelMinutes: null,
      autoCancelMinutesAllDay: null

    }

    const chk = (v) => {
      if (typeof v !== "number" || v < 1) return null;
      v = Math.floor(v);
      return v;
    }
    const j = aiimUtil.getAttributeValue(feature.attributes, FieldNames.AREAS_CONFIG);
    if (typeof j === "string" && j.startsWith("{") && j.endsWith("}")) {
      try {
        const json = JSON.parse(j);
        config.maxBookingsPerPerson = chk(json && json.maxBookingsPerPerson);
        config.maxDaysPerBooking = chk(json && json.maxDaysPerBooking);
        config.maxDaysInAdvance = chk(json && json.maxDaysInAdvance);
        // config.autoCancelMinutes = chk(json && json.autoCancelMinutes);
        // config.autoCancelMinutesAllDay = chk(json && json.autoCancelMinutesAllDay);
      } catch(ex) {
        console.error("Error area CONFIG",ex)
      }
    }
    return config;
  }

  addArea(areaName: string, areaType: SpaceAssignmentTypes, data) {
    const areaId = areaType+"-"+val.generateRandomId();
    const promise = new Promise<void>((resolve,reject) => {
      transactions.addArea(areaType,areaId,areaName,data).then(() => {
        return this._refresh();
      }).then(() => {
        resolve();
      }).catch(ex =>  {
        reject(ex);
      })
    });
    return promise;
  }

  deleteArea(areaItem: __esri.Graphic, deletePeopleData) {
    const promise = new Promise<void>((resolve,reject) => {
      const f = aiimUtil.findField(this.table.fields,FieldNames.AREA_TYPE);
      const areaType = aiimUtil.getAttributeValue(areaItem.attributes,f.name);
      transactions.deleteArea(areaItem,areaType,deletePeopleData).then(() => {
        return this._refresh();
      }).then(() => {
        console.log("AreasTable deleteArea complete ................")
        resolve();
      }).catch(ex =>  {
        reject(ex);
      })
    });
    return promise;
  }

  renameArea(areaItem: __esri.Graphic, areaName: string) {
    const promise = new Promise<void>((resolve,reject) => {
      const f = aiimUtil.findField(this.table.fields,FieldNames.AREA_TYPE);
      const areaType = aiimUtil.getAttributeValue(areaItem.attributes,f.name);
      transactions.renameArea(areaItem,areaType,areaName).then(() => {
        return this._refresh();
      }).then(() => {
        resolve();
      }).catch(ex =>  {
        reject(ex);
      })
    });
    return promise;
  }

  updateArea(areaItem: __esri.Graphic, areaName: string,data) {
    const promise = new Promise<void>((resolve,reject) => {
      const f = aiimUtil.findField(this.table.fields,FieldNames.AREA_TYPE);
      const areaType = aiimUtil.getAttributeValue(areaItem.attributes,f.name);
      transactions.updateArea(areaItem,areaType,areaName,data).then(() => {
        return this._refresh();
      }).then(() => {
        resolve();
      }).catch(ex =>  {
        reject(ex);
      })
    });
    return promise;
  }

  updateAreaRestriction(areaItem: __esri.Graphic, restricted: boolean) {
    const promise = new Promise<void>((resolve,reject) => {
      const f = aiimUtil.findField(this.table.fields,FieldNames.AREA_TYPE);
      const areaType = aiimUtil.getAttributeValue(areaItem.attributes,f.name);
      transactions.updateAreaRestriction(areaItem,areaType,restricted).then(() => {
        return this._refresh();
      }).then(() => {
        resolve();
      }).catch(ex =>  {
        reject(ex);
      })
    });
    return promise;
  }

  updateAreaRoles(areaItem: __esri.Graphic, roleData) {
    const promise = new Promise<void>((resolve,reject) => {
      transactions.updateAreaRoles(areaItem,roleData).then(() => {
        resolve();
      }).catch(ex =>  {
        reject(ex);
      })
    });
    return promise;
  }

  getArea(areaId: string) {
    return (this._areasById && this._areasById[areaId]);
  }

  getAreaName(areaId: string): string {
    const area = (this._areasById && this._areasById[areaId]);
    if (area) {
      return aiimUtil.getAttributeValue(area.attributes,FieldNames.AREA_NAME);
    }
    return "";
  }

  getAreaType(areaId: string): string | undefined {
    const area = (this._areasById && this._areasById[areaId]);
    if (area) {
      return aiimUtil.getAttributeValue(area.attributes,FieldNames.AREA_TYPE);
    }
  }

  getRestrictedField(): __esri.Field | null {
    if (this.table && this.table.fields) {
      let f = aiimUtil.findField(this.table.fields,FieldNames.AREAS_RESTRICTED);
      return f;
    }
    return null;
  }

  getBookingLimits(feature: __esri.Graphic) {
    return AreasTable.getConfig(feature);
  }

  hasBookingLimitFields() {
    if (this.table && this.table.fields) {
      let fields = this.table.fields;
      return !!aiimUtil.findField(fields,FieldNames.AREAS_CONFIG);
    }
    return false;
  }

  load(table: __esri.FeatureLayer) {
    const promise = new Promise<void>((resolve,reject) => {
      this.table = table;
      table.when().then(() => {
        if (table.loadStatus === "failed") {
          console.error("AreasTable: load failed");
        }
        return this._refresh();
      }).then(() => {
        resolve();
      }).catch(ex => {
        console.error("Error loading AREAS table",ex);
        reject(ex);
      });
      table.load();
    });
    return promise;
  }

  makeDefinition(task) {
    const definition: ILayerDefinition = {
      "name": "AREAS",
      "type": "Table",
      "capabilities": "Query,Editing,Create,Update,Delete,Sync",
      "fields": [
        {
          "alias": "OBJECTID",
          "defaultValue": null,
          "domain": null,
          "editable": false,
          "name": "OBJECTID",
          "nullable": false,
          // @ts-ignore
          "sqlType": "sqlTypeOther",
          "type": "esriFieldTypeOID"
        },
        {
          "alias": "Area Name",
          "defaultValue": "",
          "domain": null,
          "editable": true,
          "length": 500,
          "name": "area_name",
          "nullable": true,
          // @ts-ignore
          "sqlType": "sqlTypeNVarchar",
          "type": "esriFieldTypeString"
        },
        {
          "alias": "Area Type",
          "defaultValue": "",
          "domain": null,
          "editable": true,
          "length": 500,
          "name": "area_type",
          "nullable": true,
          // @ts-ignore
          "sqlType": "sqlTypeNVarchar",
          "type": "esriFieldTypeString"
        },
        {
          "alias": "Area ID",
          "defaultValue": "",
          "domain": null,
          "editable": true,
          "length": 500,
          "name": "area_id",
          "nullable": true,
          // @ts-ignore
          "sqlType": "sqlTypeNVarchar",
          "type": "esriFieldTypeString"
        }
      ],
      "htmlPopupType": "esriServerHTMLPopupTypeNone",
      "allowGeometryUpdates": true,
      "hasMetadata": false,
      "hasStaticData": false,
      "defaultVisibility": true,
      "isDataVersioned": false,
      "globalIdField": "",
      "hasZ": false,
      "copyrightText": "",
      "hasM": false,
      "displayField": "area_name",
      "standardMaxRecordCount": 32000,
      "hasAttachments": false,
      "typeIdField": "",
      "templates": [],
      "uniqueIdField": {
        "name": "OBJECTID",
        "isSystemMaintained": true
      },
      "maxRecordCountFactor": 1,
      "maxRecordCount": 2000,
      "description": "Area related attributes",
      "types": [],
      "tileMaxRecordCount": 8000,
      "objectIdField": "OBJECTID",
      "syncEnabled": true
    };
    return definition;
  }

  queryHotDeskAreas() {
    return this._refresh().then(()=>{
      return this._hotDeskAreas;
    })
  }

  queryHotelAreas() {
    return this._refresh().then(()=>{
      return this._hotelAreas;
    })
  }

  refresh() {
    if (!this.table) return Promise.resolve();
    const promise = new Promise<void>((resolve,reject) => {
      const areasById = {
        [HomeOfficeId]: this.HomeOffice
      };
      const table = this.table;
      const lib = Context.instance.lib;
      const url = Context.checkMixedContent(table.url+"/"+table.layerId);
      const queryTask: CustomQueryTask = new lib.esri.QueryTask({url: url});
      const query = new lib.esri.Query();
      query.outFields = ["*"];
      query.where = "1=1";
      if (table.gdbVersion) query.gdbVersion = table.gdbVersion;
      queryTask.execute(query).then(result => {
        //console.log("AreasTable.query.result",result);
        let fields = (result.fields || table.fields);
        let hotDeskAreas = [], hotelAreas = [];
        let idField = aiimUtil.findField(fields,FieldNames.AREA_ID);
        let nameField = aiimUtil.findField(fields,FieldNames.AREA_NAME);
        let typeField = aiimUtil.findField(fields,FieldNames.AREA_TYPE);
        result.features.forEach(row => {
          //console.log(aiimUtil.getAttributeValue(row.attributes,nameField.name),row.attributes)
          const areaId: string = aiimUtil.getAttributeValue(row.attributes,idField.name);
          const areaType: SpaceAssignmentTypes = aiimUtil.getAttributeValue(row.attributes,typeField.name);

          //console.log(aiimUtil.getAttributeValue(row.attributes,nameField.name),row.attributes)

          areasById[areaId] = row;
          if (areaType === OfficePlan.SpaceAssignmentTypes.hotdesk) {
            hotDeskAreas.push(row);
          } else if (areaType === OfficePlan.SpaceAssignmentTypes.hotel) {
            hotelAreas.push(row);
          }
        });
        this._sort(hotDeskAreas,nameField.name);
        this._sort(hotelAreas,nameField.name);
        this._areasById = areasById;
        this._hotDeskAreas = hotDeskAreas;
        this._hotelAreas = hotelAreas;
        Topic.publish(Topic.AreasRefreshed,{areasById: areasById});
        resolve();
      }).catch(ex => {
        console.error("Error querying AREAS table",ex);
        reject(ex);
      });
    });
    return promise;
  }

  _refresh() {
    return this.refresh();
  }

  _sort(areas: __esri.Graphic[], typeFieldName: string) {
    areas.sort((a,b) => {
      const aName = aiimUtil.getAttributeValue(a.attributes,typeFieldName) || "";
      const bName = aiimUtil.getAttributeValue(b.attributes,typeFieldName) || "";
      return aName.localeCompare(bName);
    })
  }

  _tmpData() {
    this._hotDeskAreas = [
      // @ts-ignore
      {
        attributes: {
          area_id: "hotdeskarea1",
          area_name: "Hot Desk Area 1",
          area_type: "hotdesk"
        }
      },      
      // @ts-ignore
      {
        attributes: {
          area_id: "hotdeskarea2",
          area_name: "Hot Desk Area 2",
          area_type: "hotdesk"
        }
      }
    ];

    this._hotelAreas = [
      // @ts-ignore
      {
        attributes: {
          area_id: "hotelarea1",
          area_name: "Hotel Area 1",
          area_type: "hotel"
        }
      },
      // @ts-ignore
      {
        attributes: {
          area_id: "hotelarea2",
          area_name: "Hotel Area 2",
          area_type: "hotel"
        }
      }
    ];
  }

}
